import type { ServiceSummaryListItems, SummaryListItemType } from '@/types'
import { formatHours } from '@/helpers/format'
import type { StoredServiceResultsV2 } from '@/helpers/service-results'

type TranslationFn = (key: string) => string
type WidgetTextFn = (...path: string[]) => string

export interface ServiceSummaryInput {
  country: string
  focusTopics: string[]
  serviceRole?: string | null
  serviceResults: StoredServiceResultsV2
}

export function renderServiceSummary(
  input: ServiceSummaryInput,
  t: TranslationFn,
  wt: WidgetTextFn,
  wtService: WidgetTextFn,
  wtSetup: WidgetTextFn,
  wtSummary: WidgetTextFn
): ServiceSummaryListItems {
  const { country, focusTopics, serviceRole, serviceResults } = input

  let focusTopicsStr = ''
  focusTopics.forEach((topic: string) => {
    focusTopicsStr += wt('focusTopics', 'option', topic) + '\n'
  })

  const roleList: SummaryListItemType[] = [
    {
      title: wtSummary('pdf', 'services', 'role', 'role'),
      value: serviceRole ? wtSetup('role', 'option', serviceRole) : '',
    },
    {
      title: wtSummary('pdf', 'services', 'role', 'focusTopics'),
      value: focusTopicsStr,
      additionalClass: 'line-break',
    },
  ]

  const basicServiceList: SummaryListItemType[] = [
    {
      title: wtSummary('pdf', 'services', 'basicService', 'full'),
      value: serviceResults.estimatedTotalHours
        ? formatHours(serviceResults.estimatedTotalHours, 0) + ' ' + t('general.hour')
        : '',
    },
    {
      title:
        wtSummary('pdf', 'services', 'basicService', 'specific') +
        ' ' +
        Math.round(
          serviceResults.phases
            .filter((phase) => phase.active)
            .reduce((sum, phase) => sum + (phase.percentage.value ?? 0), 0)
        ) +
        '%',
      value: serviceResults.totals.unadjustedHours
        ? formatHours(serviceResults.totals.unadjustedHours, 0) + ' ' + t('general.hour')
        : '',
      additionalClass: 'highlight-item',
    },
  ]

  const additionalServicePhaseRows: SummaryListItemType[] = []

  const shouldShowPhaseInSummary = (phaseOrder: number) => {
    if (country === 'LU') {
      return true
    } else {
      return [3, 4, 5].includes(phaseOrder)
    }
  }

  serviceResults.phases.forEach((phase) => {
    if (phase.active && phase.subPhases) {
      phase.subPhases.forEach((subPhase) => {
        const phaseNumber = [3, 4, 5].includes(phase.order)
          ? phase.order.toString() + subPhase.order.toString()
          : phase.order

        if (shouldShowPhaseInSummary(phase.order)) {
          const phaseTitle =
            country === 'LU'
              ? wtService('phases', country, 'phase' + phase.order, 'name')
              : wtSummary('pdf', 'services', 'basicService', 'phase') + ' ' + phaseNumber

          basicServiceList.push({
            title: phaseTitle,
            value:
              formatHours(subPhase.totals.unadjustedHours + subPhase.totals.adjustmentHours, 0) +
              ' ' +
              t('general.hour'),
          })
        }

        if (subPhase.additionalHours) {
          const phaseTitle =
            country === 'LU'
              ? wtService('phases', country, 'phase' + phase.order, 'name')
              : wtSummary('pdf', 'services', 'basicService', 'phase') + ' ' + phaseNumber

          additionalServicePhaseRows.push({
            title: phaseTitle,
            value: formatHours(subPhase.additionalHours, 0) + ' ' + t('general.hour'),
            remark: subPhase.remarks ? subPhase.remarks : undefined,
          })
        }
      })
    }
  })

  basicServiceList.push({
    title: wtSummary('pdf', 'services', 'totals', 'withoutAdditional'),
    value:
      formatHours(serviceResults.totals.unadjustedHours + serviceResults.totals.adjustmentHours, 0) +
      ' ' +
      t('general.hour'),
    additionalClass: 'highlight-item',
  })

  const additionalServiceList: SummaryListItemType[] = [
    ...additionalServicePhaseRows,
    {
      title: wtSummary('pdf', 'services', 'additionalService', 'total'),
      value:
        serviceResults.totals.additionalHours > 0
          ? formatHours(serviceResults.totals.additionalHours, 0) + ' ' + t('general.hour')
          : '',
      additionalClass: 'semi-highlight-item',
    },
  ]

  additionalServiceList.push({
    title: wtSummary('pdf', 'services', 'totals', 'withAdditional'),
    value: formatHours(serviceResults.totals.totalHours, 0) + ' ' + t('general.hour'),
    additionalClass: 'highlight-item',
  })

  return { roleList, basicServiceList, additionalServiceList }
}
